/**
 * Device location wrapper (Card 29).
 *
 * The SINGLE chokepoint through which this app reads the device position. Every
 * caller goes through `getCurrentPosition()` so that permission handling, error
 * classification and the coarse-accuracy decision live in exactly one place.
 *
 * ── Why the browser API and not `@capacitor/geolocation` ──────────────────────
 * The plugin was vetted first, because a Capacitor plugin without a
 * `Package.swift` is silently dropped by `cap sync` on this SPM iOS build and
 * then fails at runtime with no build error (this repo shipped that twice —
 * `capacitor-native-biometric`, and Card 20's entitlements). It PASSES:
 * `@capacitor/geolocation@8.2.2` ships `Package.swift` plus
 * `ios/Sources/GeolocationPlugin/*.swift`, so it would link correctly. See
 * `src/docs/SERVICE_AREA_LOCATION.md` §1 for the tarball listing.
 *
 * It is still not what we use, for one reason: adding it adds NATIVE code, and
 * native code cannot reach an installed binary over the air. Card 27 declared
 * the location permissions ahead of this feature precisely so this could ship
 * without a second native release; taking the plugin would throw that away and
 * re-block Cards 11/12 behind a build. `navigator.geolocation` runs inside the
 * Capacitor WebView on both platforms under those same declarations:
 *   • Android — `BridgeWebChromeClient#onGeolocationPermissionsShowPrompt`
 *     (@capacitor/android 8.x) requests ACCESS_COARSE_LOCATION +
 *     ACCESS_FINE_LOCATION and, on SDK 31+, explicitly grants the WebView
 *     callback when only COARSE was allowed. That is the Android 12+
 *     approximate-only path working by construction, not by luck.
 *   • iOS — WKWebView serves geolocation itself from iOS 15 (the deployment
 *     target here) using the host app's NSLocationWhenInUseUsageDescription,
 *     which Card 27 added. No Capacitor-side delegate is involved.
 * If real-device QA ever shows the iOS WebView path failing, the plugin is a
 * drop-in replacement for this file alone — but it costs a native release.
 */

export type LocationPermissionState =
  | 'unsupported'
  | 'unknown'
  | 'prompt'
  | 'granted'
  | 'denied';

export type DeviceLocationErrorCode =
  | 'unsupported'
  | 'denied'
  | 'unavailable'
  | 'timeout';

export interface DeviceCoordinates {
  latitude: number;
  longitude: number;
  /** Radius of the 95% confidence circle in metres, or null when not reported. */
  accuracyMeters: number | null;
}

export class DeviceLocationError extends Error {
  constructor(public code: DeviceLocationErrorCode, message: string) {
    super(message);
    this.name = 'DeviceLocationError';
  }
}

/**
 * An accuracy worse than this is an "approximate" fix — Android 12+ users who
 * granted approximate-only location land here, and so do indoor Wi-Fi fixes.
 * It is a SUCCESS path: a city-level service area does not need better. The UI
 * uses it only to prompt the user to sanity-check the pin.
 */
export const APPROXIMATE_ACCURACY_THRESHOLD_METERS = 1500;

export function isGeolocationSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.geolocation?.getCurrentPosition === 'function';
}

/**
 * Current permission state WITHOUT prompting.
 *
 * This is what keeps the "never a repeated prompt" rule honest: a caller can
 * see 'denied' and render the explanation instead of firing a request that the
 * platform would reject silently. Returns 'unknown' where the Permissions API
 * is unavailable — the caller must treat that as "may prompt", not as granted.
 */
export async function getLocationPermissionState(): Promise<LocationPermissionState> {
  if (!isGeolocationSupported()) return 'unsupported';

  const permissions = typeof navigator !== 'undefined' ? navigator.permissions : undefined;
  if (!permissions?.query) return 'unknown';

  try {
    const status = await permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state;
    }
    return 'unknown';
  } catch {
    // Safari has historically thrown for unsupported descriptors rather than
    // rejecting the descriptor list. Unknown is the safe answer.
    return 'unknown';
  }
}

/**
 * Read the device position once.
 *
 * `enableHighAccuracy` is deliberately FALSE. A service area is a city or a
 * county, so a network/Wi-Fi fix is already far more precise than the answer
 * needs to be — and asking for high accuracy on Android where the user granted
 * approximate-only location is the documented way to turn a usable coarse fix
 * into a failure. It is also faster and costs far less battery.
 */
export async function getCurrentPosition(options?: {
  timeoutMs?: number;
  maximumAgeMs?: number;
}): Promise<DeviceCoordinates> {
  if (!isGeolocationSupported()) {
    throw new DeviceLocationError('unsupported', 'This device cannot report its location.');
  }

  const timeout = options?.timeoutMs ?? 15000;
  const maximumAge = options?.maximumAgeMs ?? 60000;

  return new Promise<DeviceCoordinates>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        resolve({
          latitude,
          longitude,
          accuracyMeters: Number.isFinite(accuracy) ? accuracy : null,
        });
      },
      (error) => reject(toDeviceLocationError(error)),
      { enableHighAccuracy: false, timeout, maximumAge },
    );
  });
}

/** Map a `GeolocationPositionError` onto our own closed set of causes. */
export function toDeviceLocationError(error: unknown): DeviceLocationError {
  const code = (error as GeolocationPositionError | undefined)?.code;

  // The numeric literals are used rather than `GeolocationPositionError.PERMISSION_DENIED`
  // because the constructor is not defined in every runtime this code loads in
  // (jsdom under vitest, and older WebViews).
  switch (code) {
    case 1:
      return new DeviceLocationError('denied', 'Location permission was denied.');
    case 2:
      return new DeviceLocationError('unavailable', 'The device could not determine a position.');
    case 3:
      return new DeviceLocationError('timeout', 'Timed out waiting for a position.');
    default:
      return new DeviceLocationError('unavailable', 'The device could not determine a position.');
  }
}

/** True when the fix is too coarse to trust the pin without a look. */
export function isApproximateFix(accuracyMeters: number | null): boolean {
  return accuracyMeters !== null && accuracyMeters > APPROXIMATE_ACCURACY_THRESHOLD_METERS;
}
